import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import { Button, ConfirmDialog, IconChatBubbleSquareWrite, IconSearch, Modal, OptionList, SkeletonText, cn, useNavigationPort, usePersonnesPort } from '@taki/ui';
import type { PersonneVue } from '@taki/ui';
import { useAppRuntime } from '@taki/app-runtime';
import type { ProprietesDeVueCanvas } from '@taki/app-runtime';
import type { Asset, CareRecord, Product } from '@taki/data-model';
import type { TakiSdk } from '@taki/sdk';
import { Alertes, ChampTexte, Choix, Onglets, Section, enEuros, instantDe, optionsDe, raisonDe, sac } from './morceaux.js';
import type { Fiche, Onglet, OptionDeclaree } from './morceaux.js';
import { heureDe, jourPur, valeurEnTexte } from './temps.js';
import { entreesDuChoix, groupesDeLignes, lignesDuProtocole, lireChoix, manquesPourCloturer, totalDesLignes } from './poste.js';
import { Examen } from './OngletExamen.js';
import { pointsDeLExamen } from './examen.js';
import { demandeDeLaDerniereVisite } from './apercu.js';
import { PanneauSuivi } from './PanneauSuivi.js';
import type { TacheDuSuivi } from './PanneauSuivi.js';
import { Complementaires } from './Complementaires.js';
import { CalculateurDeDose } from './OngletDoses.js';
import { ageEnAnnees } from './doses.js';
import { nombreEnTexte } from './nombre.js';
import { Carnet } from './OngletCarnet.js';
import { dateDeRappel, produitsPour } from './carnet.js';
import { CadreAFaire, CadreDApercu, CadreSonHistorique, EnTeteDuPatient, LigneDApercu, ListeDeTaches, OngletHistorique, OngletTaches } from './briques.js';
import { useDossierDeLAnimal } from './useDossierDeLAnimal.js';
import type { ProprietesDeVueCanvas as ProprietesDeVue } from '@taki/app-runtime';
import { gestesDeLaFiche } from './gestes.js';
import type { EtatDeLAnimal } from './gestes.js';

/** Ce que la vue recoit du socle pour poser ses gestes dans la barre de la fiche. */
type PoserActions = ProprietesDeVue['poserActions'] | undefined;

/**
 * LE DOSSIER DE CONSULTATION : la VUE DE FICHE Canvas des consultations
 * (`vue:consultations.dossier`, regle G23 accordee dans `canvas.views` de
 * `taki.app.json`, carte dans `vuesCustom` de `taki.screens.json`). Le patient
 * en tete, la cloture, puis sept onglets : apercu, consultation, soins et
 * produits, historique, vaccins et rappels, taches, calculateur de dose. La
 * Fiche du socle reste la, dans le selecteur de vues.
 *
 * CE QUE LE SOCLE FAIT DEJA, ICI N'EST PAS REFAIT : la barre d'etapes
 * (chevrons) est la rangee 3 du gabarit (`etapes` declare sur `stage`), la
 * sortie de la fiche est sa puce, l'enregistrement d'un champ de la
 * consultation passe par `onPatch`, le meme chemin que l'edition en place
 * (optimisme, annulation, indicateur d'enregistrement).
 *
 * Ce qui depend de l'animal (proprietaire, soins, taches, carnet, historique)
 * est lu par le SDK, cadre par `orgId`. Les autres apps (le dossier de
 * l'animal, une tache, une autre consultation) s'ouvrent par le port de
 * navigation du shell, jamais par un id d'app.
 */

/** Le sommaire du compte rendu : les ancres des six etapes de l'onglet Consultation. */
const SOMMAIRE: readonly { ancre: string; libelle: string }[] = [
  { ancre: 'cr-anamnese', libelle: 'Anamnèse' },
  { ancre: 'cr-examen', libelle: 'Examen' },
  { ancre: 'cr-complementaires', libelle: 'Complémentaires' },
  { ancre: 'cr-diagnostic', libelle: 'Diagnostic' },
  { ancre: 'cr-therapie', libelle: 'Thérapie' },
  { ancre: 'cr-suivi', libelle: 'Suivi' },
];

/**
 * LE SOMMAIRE DU COMPTE RENDU, colle en haut : une barre encadree, a part du
 * contenu, une etape par bouton numerote. L'etape ou l'on se trouve est en
 * surbrillance ; un clic y defile en douceur. L'ecoute du defilement est en
 * capture : elle entend le conteneur qui defile, quel qu'il soit.
 */
function Sommaire(): JSX.Element {
  const [actif, setActif] = useState(SOMMAIRE[0]?.ancre ?? '');
  useEffect(() => {
    const suivre = (): void => {
      const barre = document.getElementById('cr-sommaire')?.getBoundingClientRect().bottom ?? 0;
      let courant = SOMMAIRE[0]?.ancre ?? '';
      for (const s of SOMMAIRE) {
        const haut = document.getElementById(s.ancre)?.getBoundingClientRect().top;
        if (haut !== undefined && haut <= barre + 24) courant = s.ancre;
      }
      setActif(courant);
    };
    document.addEventListener('scroll', suivre, true);
    return () => document.removeEventListener('scroll', suivre, true);
  }, []);
  /* La marge de defilement suit la hauteur reelle de la barre : le titre de l'etape arrive juste dessous. */
  const allerA = (ancre: string): void => {
    const cible = document.getElementById(ancre);
    if (cible === null) return;
    const barre = document.getElementById('cr-sommaire')?.getBoundingClientRect().height ?? 0;
    cible.style.scrollMarginTop = `${Math.round(barre) + 16}px`;
    setActif(ancre);
    cible.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  /* Le fond plein autour de la barre : rien du contenu ne passe au-dessus d'elle en defilant. */
  return (
    <div id="cr-sommaire" className="sticky top-0 z-10 -mx-2 bg-surface px-2 py-2">
      <div
        role="group"
        aria-label="Étapes du compte rendu"
        className="flex gap-1 overflow-x-auto rounded-md border border-border bg-surface p-1 shadow-sm"
      >
        {SOMMAIRE.map((s, rang) => (
          <button
            key={s.ancre}
            type="button"
            aria-current={actif === s.ancre ? 'step' : undefined}
            onClick={() => allerA(s.ancre)}
            className={cn(
              'flex h-9 flex-1 shrink-0 items-center whitespace-nowrap justify-center gap-2 rounded-sm px-3 text-sm font-semibold transition-colors',
              actif === s.ancre ? 'bg-ink-900 text-white' : 'text-ink-700 hover:bg-surface-3',
            )}
          >
            <span
              className={cn(
                'flex h-5 w-5 items-center justify-center rounded-full text-xs',
                actif === s.ancre ? 'bg-white text-ink-900' : 'bg-surface-3 text-ink-700',
              )}
            >
              {rang + 1}
            </span>
            {s.libelle}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * LE NUMERO DE LOT d'un vaccin ou d'un antiparasitaire, dans la ligne de soin :
 * le carnet de vaccination le reprend. Enregistre a Entree ou en quittant le champ.
 */
function ChampDuLot({ valeur, onEcrire }: { valeur: string; onEcrire: (lot: string) => void }): JSX.Element {
  const [lot, setLot] = useState(valeur);
  useEffect(() => setLot(valeur), [valeur]);
  const ecrire = (): void => {
    if (lot.trim() !== valeur.trim()) onEcrire(lot);
  };
  return (
    <input
      className="h-8 w-full rounded-sm border border-border bg-surface-1 px-2 text-sm text-ink-900 placeholder:text-ink-300 focus-visible:border-accent-border focus-visible:outline-none"
      value={lot}
      placeholder="N° de lot"
      aria-label="Numéro de lot"
      onChange={(e) => setLot(e.target.value)}
      onBlur={ecrire}
      onKeyDown={(e) => {
        if (e.key === 'Enter') ecrire();
      }}
    />
  );
}

/** Une etape du compte rendu : un titre numerote, une ancre pour le sommaire. */
function Etape({ ancre, titre, children }: { ancre: string; titre: string; children: ReactNode }): JSX.Element {
  return (
    <section id={ancre} className="flex scroll-mt-20 flex-col gap-3">
      <h3 className="border-b border-border-soft pb-2 text-base font-semibold text-ink-900">{titre}</h3>
      {children}
    </section>
  );
}

/** Les etapes ou la consultation est CLOTUREE : les soins et produits sont figes. */
const ETAPES_CLOTUREES = ['aFacturer', 'payee'];

/** Les champs du compte rendu que la lecture de la Fiche (optimiste) tient a jour. */
const CLES_DE_LA_CONSULTATION = [
  'reason',
  'veterinarian',
  'description',
  'anamnesis',
  'exam',
  'extraExams',
  'diagnosis',
  'therapy',
  'followUp',
  'prognosis',
  'closedAt',
  'closedBy',
] as const;
/** Ceux qui se rangent en liste dans le sac (un choix multiple). */
const LISTES = ['extraExams'];

/**
 * LA CONSULTATION TELLE QUE L'ECRAN LA MONTRE : la fiche brute, dont le sac est
 * recouvert par ce que la Fiche a resolu (`lecture`), ecriture en vol comprise.
 * Lue sur la fiche brute, une case ne suivait qu'apres la relecture du serveur.
 * Un champ que la Fiche ne montre pas garde sa valeur brute.
 */
function consultationLue(fiche: Fiche, lecture: ProprietesDeVueCanvas['lecture']): Fiche {
  const base: Record<string, unknown> = { ...sac(fiche) };
  if (lecture !== null) {
    for (const cle of CLES_DE_LA_CONSULTATION) {
      const champ = lecture.champ(cle);
      if (champ === undefined) continue;
      const valeur = champ.valeur;
      if (valeur === null || valeur === '') delete base[cle];
      else if (LISTES.includes(cle)) base[cle] = String(valeur).split(/[\n,]/).map((x) => x.trim()).filter((x) => x !== '');
      else base[cle] = valeur instanceof Date ? valeur.toISOString() : valeur;
    }
    if (lecture.statut !== null) base['stage'] = lecture.statut.value;
  }
  return { ...fiche, customFields: base };
}

interface ProprietesDuDossier {
  sdk: TakiSdk;
  orgId: string;
  consultation: Fiche;
  lecture: ProprietesDeVueCanvas['lecture'];
  /** L'ecriture d'un champ de la consultation ouverte (`onPatch`). */
  ecrireChamp: (cle: string, valeur: string | string[] | Date | null) => void;
  /** L'annuaire de l'org : a qui donner une tache, et les noms. */
  personnes: readonly PersonneVue[];
  moi: string;
  onOuvrirConsultation: (id: string) => void;
  onOuvrirFiche: (family: string, type: string, id: string, depuis: string | null) => void;
  /** Le dossier de l'animal, dans l'app qui le montre ; absent : le bouton n'est pas propose. */
  onOuvrirDossierDeLAnimal: ((animalId: string, nom: string) => void) | null;
  /** La barre d'action de la fiche : les gestes sur la fiche entiere y vivent, jamais dans le corps. */
  poserActions: PoserActions;
}

function Dossier(props: ProprietesDuDossier): JSX.Element {
  const { sdk, orgId, consultation, lecture, moi, personnes } = props;
  const id = String(consultation['id']);
  const assetId = String(consultation['assetId'] ?? '');
  const quand = instantDe(consultation) ?? new Date();
  const [onglet, setOnglet] = useState('apercu');
  const nomDePersonne = (personId: unknown): string => {
    if (typeof personId !== 'string' || personId === '') return '';
    const p = personnes.find((x) => x.personId === personId);
    return p === undefined ? '' : p.displayName.trim() !== '' ? p.displayName : p.email;
  };

  /* LES OPTIONS D'UN CHAMP A CHOIX : celles que la Fiche propose, sinon celles de la declaration de l'app. */
  const optionsDuChamp = (cle: string): readonly OptionDeclaree[] => {
    const choix = lecture?.champ(cle)?.options ?? [];
    return choix.length > 0 ? choix.map((o) => ({ value: o.value, label: o.label })) : optionsDe('consultation', cle);
  };

  /* L'ANIMAL, SON HISTORIQUE DE CONSULTATIONS ET LE CATALOGUE : trois lectures cadrees par l'org. */
  const [animal, setAnimal] = useState<Asset | null>(null);
  const [animalLu, setAnimalLu] = useState(assetId === '');
  useEffect(() => {
    setAnimal(null);
    setAnimalLu(assetId === '');
    if (assetId === '') return;
    let vivant = true;
    void sdk.assets.get(assetId).then(
      (a) => {
        if (!vivant) return;
        setAnimal(a);
        setAnimalLu(true);
      },
      () => vivant && setAnimalLu(true),
    );
    return () => {
      vivant = false;
    };
  }, [sdk, assetId]);
  const [duMemeAnimal, setDuMemeAnimal] = useState<readonly CareRecord[]>([]);
  const [duMemeAnimalLu, setDuMemeAnimalLu] = useState(assetId === '');
  useEffect(() => {
    setDuMemeAnimal([]);
    setDuMemeAnimalLu(assetId === '');
    if (assetId === '') return;
    let vivant = true;
    void sdk.careRecords.list({ type: 'consultation', where: { assetId }, limit: 200 }).then(
      (liste) => {
        if (!vivant) return;
        setDuMemeAnimal(liste);
        setDuMemeAnimalLu(true);
      },
      () => vivant && setDuMemeAnimalLu(true),
    );
    return () => {
      vivant = false;
    };
  }, [sdk, assetId]);
  const [catalogue, setCatalogue] = useState<readonly Product[]>([]);
  const [catalogueLu, setCatalogueLu] = useState(false);
  useEffect(() => {
    let vivant = true;
    void sdk.products.list({ type: 'article', limit: 1000 }).then(
      (liste) => {
        if (!vivant) return;
        setCatalogue(liste);
        setCatalogueLu(true);
      },
      () => vivant && setCatalogueLu(true),
    );
    return () => {
      vivant = false;
    };
  }, [sdk]);

  const fiche = sac(animal);
  const historique = useMemo(
    () =>
      duMemeAnimal
        .filter((c) => c.id !== id)
        .sort((a, b) => (b.performedAt?.getTime() ?? 0) - (a.performedAt?.getTime() ?? 0)),
    [duMemeAnimal, id],
  );
  /* LE DOSSIER DE L'ANIMAL : proprietaire, soins, taches, carnet, courbe du poids. */
  const dossier = useDossierDeLAnimal({ sdk, orgId, animal, consultationId: id, historique, catalogue, nomDePersonne, quand });
  const { soins, taches, tachesOuvertes, carnet, produitsDuCarnet, recharger, erreur, setErreur } = dossier;

  /* LES POINTS EXAMINES de l'examen en liste : la pastille de l'onglet et la cloture les lisent. */
  const [examines, setExamines] = useState(0);
  useEffect(() => {
    let vivant = true;
    setExamines(0);
    void sdk.inspections.list({ type: 'examen', where: { 'customFields.consultationId': id }, limit: 5 }).then(
      (examens) => vivant && setExamines(pointsDeLExamen([], examens[0]?.checkpoints ?? []).filter((p) => p.etat !== 'nonExamine').length),
      () => undefined,
    );
    return () => {
      vivant = false;
    };
  }, [sdk, id]);

  const lignes = useMemo(() => soins.filter((s) => sac(s)['consultationId'] === id), [soins, id]);
  const produit = (productId: unknown): Product | undefined => catalogue.find((p) => p.id === productId);
  const prixDe = (productId: unknown): number | null => {
    const prix = sac(produit(productId))['priceInclTax'];
    return typeof prix === 'number' ? prix : null;
  };
  const demande = demandeDeLaDerniereVisite(dossier.visites, quand);
  const etape = String(sac(consultation)['stage'] ?? '');
  const cloturee = ETAPES_CLOTUREES.includes(etape);
  const totalDuJour = totalDesLignes(lignes.map((l) => ({ quantite: Number(sac(l)['quantity'] ?? 1), prix: prixDe(sac(l)['productId']) })));
  /* Un examen coche compte comme un examen ecrit : la cloture ne le reclame plus. */
  const manques = manquesPourCloturer({
    lignes: lignes.length,
    compteRendu: examines > 0 ? { ...sac(consultation), exam: 'liste' } : sac(consultation),
  });
  const [cloture, setCloture] = useState(false);
  const [reouverture, setReouverture] = useState(false);
  /**
   * ROUVRIR : toute personne qui peut ecrire la consultation (veterinaire ou
   * ASV qui facture). Seulement depuis « A facturer » : une consultation payee
   * repasse d'abord a « A facturer » par la barre d'etapes. L'etape revient a
   * « En consultation », la trace de cloture s'efface ; qui a rouvert et quand
   * reste dans l'historique de la fiche.
   */
  const rouvrir = (): void => {
    setReouverture(false);
    props.ecrireChamp('stage', 'enConsultation');
    props.ecrireChamp('closedAt', null);
    props.ecrireChamp('closedBy', null);
  };
  const cloturer = (): void => {
    if (manques.bloquants.length > 0) return;
    setCloture(false);
    props.ecrireChamp('stage', 'aFacturer');
    props.ecrireChamp('closedAt', new Date());
    props.ecrireChamp('closedBy', moi === '' ? null : moi);
  };
  const brutDeCloture = sac(consultation)['closedAt'];
  const clotureLe = typeof brutDeCloture === 'string' ? new Date(brutDeCloture) : null;

  const vide = (cle: string): boolean => {
    const v = sac(consultation)[cle];
    return typeof v !== 'string' || v.trim() === '';
  };
  const compteRenduVide = vide('anamnesis') && vide('diagnosis');
  const examenVide = examines === 0 && vide('exam');

  /* ---------------- Ecritures ---------------- */

  const ecrire = (cle: string, valeur: string | string[] | null): void => props.ecrireChamp(cle, valeur);

  const [produitChoisi, setProduitChoisi] = useState('');
  const [choixOuvert, setChoixOuvert] = useState(false);
  const [protocolesOuverts, setProtocolesOuverts] = useState(false);
  /* LE RAPPEL SE POSE SEUL : un produit qui porte un delai de rappel le donne a la ligne. */
  const rappelDuProduit = (productId: string): { reminderDate?: string } => {
    const date = dateDeRappel(jourPur(quand), sac(produit(productId))['reminderDelay']);
    return date === null ? {} : { reminderDate: date };
  };
  /* « Ajouter aujourd'hui » du carnet : une ligne de soin, quantite un. */
  const ajouterDepuisLeCarnet = async (productId: string): Promise<void> => {
    if (productId === '' || cloturee) return;
    try {
      await sdk.careRecords.create({
        orgId,
        type: 'soin',
        ...(assetId === '' ? {} : { assetId }),
        customFields: { productId, consultationId: id, quantity: 1, careDate: jourPur(quand), ...rappelDuProduit(productId), billingStatus: 'aFacturer' },
      });
      setErreur(null);
      await recharger();
    } catch (cause) {
      setErreur(`Ligne non ajoutée : ${raisonDe(cause)}`);
    }
  };
  /* Le numero de lot d'une ligne (tracabilite) : modifiable meme apres la cloture, il ne touche pas a la facture. */
  const ecrireLot = async (ligneId: string, lot: string): Promise<void> => {
    try {
      await sdk.careRecords.update(ligneId, { customFields: { lotNumber: lot.trim() === '' ? null : lot.trim() } });
      await recharger();
    } catch (cause) {
      setErreur(`Lot non enregistré : ${raisonDe(cause)}`);
    }
  };
  const [quantite, setQuantite] = useState('1');
  const ajouterLigne = async (): Promise<void> => {
    if (produitChoisi === '' || cloturee) return;
    const nombre = Number(quantite.replace(',', '.'));
    try {
      await sdk.careRecords.create({
        orgId,
        type: 'soin',
        ...(assetId === '' ? {} : { assetId }),
        customFields: {
          productId: produitChoisi,
          consultationId: id,
          quantity: Number.isFinite(nombre) && nombre > 0 ? nombre : 1,
          careDate: jourPur(quand),
          ...rappelDuProduit(produitChoisi),
          billingStatus: 'aFacturer',
        },
      });
      setProduitChoisi('');
      setQuantite('1');
      setErreur(null);
      await recharger();
    } catch (cause) {
      setErreur(`Ligne non ajoutée : ${raisonDe(cause)}`);
    }
  };
  /*
   * LES PROTOCOLES du Catalogue : un produit `protocole`, et une fiche
   * `compositions` de type `ligne` par ligne (natif `productId` = le protocole,
   * `articleId` = le produit, `quantite`). Les lignes gardent l'ordre ou le
   * Catalogue les a creees.
   */
  const [protocoles, setProtocoles] = useState<ReadonlyArray<{ protocole: Product; lignes: Array<{ productId: string; quantity: number }> }>>([]);
  const [protocolesLus, setProtocolesLus] = useState(false);
  useEffect(() => {
    let vivant = true;
    void Promise.all([sdk.products.list({ type: 'protocole', limit: 500 }), sdk.compositions.list({ type: 'ligne', limit: 1000 })]).then(
      ([desProtocoles, desLignes]) => {
        if (!vivant) return;
        setProtocoles(
          desProtocoles
            .map((protocole) => ({
              protocole,
              lignes: lignesDuProtocole(
                desLignes
                  .filter((l) => l.productId === protocole.id)
                  .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime() || a.id.localeCompare(b.id)),
              ),
            }))
            .filter((p) => p.lignes.length > 0)
            .sort((a, b) => a.protocole.name.localeCompare(b.protocole.name, 'fr')),
        );
        setProtocolesLus(true);
      },
      () => {
        if (!vivant) return;
        setProtocoles([]);
        setProtocolesLus(true);
      },
    );
    return () => {
      vivant = false;
    };
  }, [sdk]);
  const rangDansLeProtocole = (protocolId: string, productId: string): number =>
    protocoles.find((p) => p.protocole.id === protocolId)?.lignes.findIndex((l) => l.productId === productId) ?? -1;
  const nomDuProtocole = (protocolId: string | null): string =>
    protocoles.find((p) => p.protocole.id === protocolId)?.protocole.name ?? 'Protocole';
  /** Ajoute les lignes du protocole a celles de la consultation, autant de fois qu'on veut : elles restent modifiables. */
  const ajouterProtocole = async (protocolId: string): Promise<void> => {
    const choisi = protocoles.find((p) => p.protocole.id === protocolId);
    if (choisi === undefined || cloturee) return;
    try {
      for (const ligne of choisi.lignes) {
        await sdk.careRecords.create({
          orgId,
          type: 'soin',
          ...(assetId === '' ? {} : { assetId }),
          customFields: {
            productId: ligne.productId,
            protocolId,
            consultationId: id,
            quantity: ligne.quantity,
            careDate: jourPur(quand),
            ...rappelDuProduit(ligne.productId),
            billingStatus: 'aFacturer',
          },
        });
      }
      setErreur(null);
    } catch (cause) {
      setErreur(`Protocole ajouté en partie : ${raisonDe(cause)}`);
    }
    await recharger();
  };
  const retirerLigne = async (ligneId: string): Promise<void> => {
    try {
      await sdk.careRecords.archive(ligneId);
      await recharger();
    } catch (cause) {
      setErreur(`Ligne non retirée : ${raisonDe(cause)}`);
    }
  };

  /* UNE TACHE DU DOSSIER VA AU VETERINAIRE DE LA CONSULTATION (le « veto qui a
     fait l'acte »), sinon a la personne connectee. */
  const veterinaire = sac(consultation)['veterinarian'];
  const assigneParDefaut = typeof veterinaire === 'string' && veterinaire !== '' ? veterinaire : moi;
  /*
   * LE SUIVI QUI DEVIENT UNE TACHE : datee du delai choisi, comptee depuis le
   * jour de la consultation, assignee comme les autres taches du dossier. Un
   * rappel de vaccin pose aussi sa date sur les vaccins de la consultation :
   * l'onglet « Vaccins et rappels » et la carte « Prochain rappel » la lisent.
   */
  const vaccinsDuJour = lignes.filter((l) => sac(produit(sac(l)['productId']))['category'] === 'Vaccin');
  const creerSuite = async (tache: TacheDuSuivi): Promise<boolean> => {
    if (!(await dossier.creerTache(tache, id))) return false;
    if (!tache.avecVaccins) return true;
    try {
      for (const ligne of vaccinsDuJour) await sdk.careRecords.update(ligne.id, { customFields: { reminderDate: jourPur(tache.date) } });
      await recharger();
      return true;
    } catch (cause) {
      setErreur(`Rappel non posé sur les vaccins : ${raisonDe(cause)}`);
      return false;
    }
  };
  const tachesDeLaConsultation = taches.filter((t) => sac(t)['consultationId'] === id);
  const ouvrirTache = (t: { id: string }): void => props.onOuvrirFiche('todos', 'dossier', t.id, animal?.name ?? null);

  /*
   * TANT QUE L'ANIMAL, SES SOINS, SON HISTORIQUE, LE CATALOGUE ET LES
   * PROTOCOLES NE SONT PAS LUS, l'ecran montre le squelette du socle : jamais
   * un total a zero, un carnet vide ou un « Premiere consultation » qui ment.
   */
  const chargement = !animalLu || !duMemeAnimalLu || !catalogueLu || !protocolesLus || !dossier.lu;

  /*
   * LA BARRE D'ACTION DE LA FICHE : les gestes sont COTE A COTE dans la rangee,
   * a gauche, jamais au menu « ... » (decision du fondateur du 28/10 a
   * 21 h 30) : « Clôturer » (ou « Rouvrir » depuis « A facturer »), puis
   * « Dossier de l'animal ». Le choix et l'ordre sont dans
   * `gestesDeLaFiche`, testes a part ; la rangee n'a pas de plafond (celui du
   * socle est retire, `core/actions-fiche-sans-plafond`). Le corps n'en porte
   * aucun. Les rappels passent par des refs : la barre n'est reposee que quand
   * l'etat change, pas a chaque rendu du dossier.
   */
  const poser = useRef(props.poserActions);
  poser.current = props.poserActions;
  const ouvrirAnimal = useRef(props.onOuvrirDossierDeLAnimal);
  ouvrirAnimal.current = props.onOuvrirDossierDeLAnimal;
  const bloquant = manques.bloquants[0] ?? null;
  const animalId = animal?.id ?? null;
  const animalNom = animal?.name ?? '';
  const peutOuvrirAnimal = props.onOuvrirDossierDeLAnimal !== null;
  const etatDeLAnimal: EtatDeLAnimal = animalId !== null ? 'lu' : assetId !== '' && !animalLu ? 'en-lecture' : 'aucun';
  useEffect(() => {
    const rangee = gestesDeLaFiche({
      cloturee,
      etape,
      chargement,
      bloquant,
      etatDeLAnimal,
      cloturer: () => setCloture(true),
      rouvrir: () => setReouverture(true),
      ouvrirDossierDeLAnimal: peutOuvrirAnimal
        ? () => {
            if (animalId !== null) ouvrirAnimal.current?.(animalId, animalNom);
          }
        : null,
    });
    poser.current?.({ rangee });
  }, [cloturee, etape, chargement, bloquant, etatDeLAnimal, peutOuvrirAnimal, animalId, animalNom]);
  useEffect(() => () => poser.current?.(null), []);

  const ONGLETS: readonly Onglet[] = [
    { id: 'apercu', libelle: 'Aperçu' },
    { id: 'consultation', libelle: 'Consultation', point: compteRenduVide && examenVide },
    { id: 'soins', libelle: 'Soins et produits', compte: chargement ? undefined : lignes.length },
    { id: 'historique', libelle: 'Historique', compte: chargement ? undefined : historique.length },
    { id: 'rappels', libelle: 'Vaccins et rappels', point: !chargement && carnet.some((l) => l.etat === 'retard' || (l.etat === 'jamais' && l.obligatoire)) },
    { id: 'taches', libelle: 'Tâches', compte: chargement ? undefined : tachesOuvertes.length },
    { id: 'doses', libelle: 'Calculateur de dose' },
  ];

  /* ---------------- Rendu ---------------- */

  /* pb-36 : les 128 px du coin de la mascotte de Lia restent vides, « Ajouter » ne passe jamais dessous. */
  return (
    <div className="flex flex-col gap-6 px-8 pb-36 pt-6">
      {cloturee ? (
        <p className="text-xs text-ink-500">
          Clôturée
          {clotureLe === null || Number.isNaN(clotureLe.getTime()) ? '' : ` le ${clotureLe.toLocaleDateString('fr-BE')} à ${heureDe(clotureLe)}`}
          {nomDePersonne(sac(consultation)['closedBy']) === '' ? '' : ` par ${nomDePersonne(sac(consultation)['closedBy'])}`}
          {' · '}soins et produits figés, compte rendu toujours modifiable.
        </p>
      ) : null}

      {erreur !== null ? <p className="text-sm text-danger">{erreur}</p> : null}

      <ConfirmDialog
        open={cloturee && etape === 'aFacturer' && reouverture}
        tone="neutral"
        title="Rouvrir la consultation ?"
        body="Les soins et produits redeviendront modifiables."
        confirmLabel="Rouvrir"
        onConfirm={rouvrir}
        onCancel={() => setReouverture(false)}
      />
      {/* La cloture : le recapitulatif vit dans la fenetre, le bloquant grise « Confirmer » avec sa raison (G16, la coque porte le title). */}
      <Modal
        open={!cloturee && cloture}
        onClose={() => setCloture(false)}
        title={`Clôturer : ${lignes.length} ${lignes.length > 1 ? 'lignes' : 'ligne'} · ${enEuros(totalDuJour)}`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setCloture(false)}>
              Annuler
            </Button>
            <span title={manques.bloquants[0]} className={cn(manques.bloquants.length > 0 && 'cursor-not-allowed')}>
              <Button disabled={manques.bloquants.length > 0} onClick={cloturer}>
                Confirmer la clôture
              </Button>
            </span>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {[...manques.bloquants, ...manques.avertissements].map((m) => (
            <p key={m} className={cn('text-sm', manques.bloquants.includes(m) ? 'text-danger' : 'text-ink-700')}>
              {m}
            </p>
          ))}
          <p className="text-sm text-ink-500">Les soins et produits seront figés. Le compte rendu restera modifiable.</p>
        </div>
      </Modal>

      {/* Le patient : la carte, avec le motif du jour. */}
      <EnTeteDuPatient
        animal={animal}
        proprietaire={dossier.proprietaire}
        quand={quand}
        derniere={dossier.derniere}
        prochain={dossier.prochain}
        chargement={chargement}
        ligne={[`${valeurEnTexte(sac(consultation)['reason']) || 'Consultation'} du ${quand.toLocaleDateString('fr-BE')}`, nomDePersonne(sac(consultation)['veterinarian'])]
          .filter((x) => x !== '')
          .join(' · ')}
      />
      <Alertes valeurs={fiche['alerts']} notes={fiche['internalNotes']} />

      <Onglets onglets={ONGLETS} actif={onglet} onChoisir={setOnglet} />

      {onglet === 'apercu' ? (
        /*
         * L'APERCU, a lire dans le couloir : les alertes sont deja dans la
         * banniere rouge du haut ; ici, pourquoi il vient, ce qu'il y a a
         * faire ou proposer, puis son historique.
         */
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <CadreDApercu icone={<IconChatBubbleSquareWrite size={18} />} titre="Pourquoi il vient">
            <dl className="flex flex-col text-sm">
              <LigneDApercu titre="Motif">{valeurEnTexte(sac(consultation)['reason']) || 'Non renseigné'}</LigneDApercu>
              {valeurEnTexte(sac(consultation)['description']) === '' ? null : (
                <LigneDApercu titre="Objet">{valeurEnTexte(sac(consultation)['description'])}</LigneDApercu>
              )}
              {valeurEnTexte(sac(consultation)['anamnesis']) === '' ? null : (
                <LigneDApercu titre="Le propriétaire">{valeurEnTexte(sac(consultation)['anamnesis'])}</LigneDApercu>
              )}
              {demande === null ? null : (
                <LigneDApercu titre="Demandé">
                  {demande.suivi} à la visite du {demande.quand.toLocaleDateString('fr-BE')}
                  {demande.diagnostic === '' ? '' : ` (${demande.diagnostic})`}
                </LigneDApercu>
              )}
            </dl>
          </CadreDApercu>
          <CadreAFaire chargement={chargement} titre="À faire ou proposer aujourd'hui" aProposer={dossier.aProposer} tachesOuvertes={tachesOuvertes} nomDePersonne={nomDePersonne} />
          <div className="lg:col-span-2">
            <CadreSonHistorique
              chargement={chargement}
              lignes={dossier.lignesDuPasse}
              poids={dossier.poids}
              vide="Première consultation de cet animal."
              onOuvrirConsultation={props.onOuvrirConsultation}
            />
          </div>
        </div>
      ) : null}

      {onglet === 'consultation' ? (
        /*
         * LE COMPTE RENDU EN UN SEUL FIL, dans l'ordre de la consultation
         * (l'examen y est fondu). Le sommaire reste en haut ; l'examen complet
         * se replie en son resume.
         */
        <div className="flex flex-col gap-8">
          <Sommaire />
          <Etape ancre="cr-anamnese" titre="1. Anamnèse">
            <ChampTexte key={`${id}-description`} libelle="Objet" valeur={sac(consultation)['description']} uneLigne onEcrire={(v) => ecrire('description', v)} />
            <ChampTexte key={`${id}-anamnesis`} libelle="Ce que rapporte le propriétaire" valeur={sac(consultation)['anamnesis']} onEcrire={(v) => ecrire('anamnesis', v)} />
          </Etape>
          <Examen
            sdk={sdk}
            orgId={orgId}
            consultationId={id}
            assetId={assetId}
            espece={fiche['species']}
            moi={moi}
            titre="2. Examen clinique"
            ancre="cr-examen"
            texteLibre={sac(consultation)['exam']}
            ecrireTexteLibre={(v) => ecrire('exam', v)}
            onExamines={setExamines}
          />
          <Etape ancre="cr-complementaires" titre="3. Examens complémentaires">
            <Complementaires
              sdk={sdk}
              orgId={orgId}
              consultationId={id}
              assetId={assetId}
              options={optionsDuChamp('extraExams')}
              valeur={sac(consultation)['extraExams']}
              onChoisir={(v) => ecrire('extraExams', v)}
            />
          </Etape>
          <Etape ancre="cr-diagnostic" titre="4. Diagnostic">
            <ChampTexte key={`${id}-diagnosis`} libelle="Diagnostic" valeur={sac(consultation)['diagnosis']} onEcrire={(v) => ecrire('diagnosis', v)} />
            <ChampTexte key={`${id}-prognosis`} libelle="Pronostic" valeur={sac(consultation)['prognosis']} onEcrire={(v) => ecrire('prognosis', v)} />
          </Etape>
          <Etape ancre="cr-therapie" titre="5. Thérapie">
            <ChampTexte key={`${id}-therapy`} libelle="Traitement et conseils" valeur={sac(consultation)['therapy']} onEcrire={(v) => ecrire('therapy', v)} />
          </Etape>
          <Etape ancre="cr-suivi" titre="6. Suivi">
            <Choix libelle="Suivi" options={optionsDuChamp('followUp')} valeur={sac(consultation)['followUp']} onEcrire={(v) => ecrire('followUp', v)} />
            <PanneauSuivi
              suivi={sac(consultation)['followUp']}
              depuis={quand}
              animal={animal?.name ?? ''}
              vaccins={vaccinsDuJour.length}
              personnes={personnes}
              assigneParDefaut={assigneParDefaut}
              onCreer={creerSuite}
            />
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-ink-500">Tâches de cette consultation</span>
              {tachesDeLaConsultation.length === 0 ? (
                <p className="text-sm text-ink-400">Aucune tâche.</p>
              ) : (
                <ListeDeTaches
                  taches={tachesDeLaConsultation}
                  nomDePersonne={nomDePersonne}
                  onBasculer={(t) => void dossier.basculerTache(t)}
                  onOuvrir={ouvrirTache}
                />
              )}
            </div>
          </Etape>
        </div>
      ) : null}

      {onglet === 'soins' ? (
        <Section
          titre="Soins et produits"
          actions={
            cloturee ? undefined : (
              /*
               * LES PROTOCOLES DU CATALOGUE, a cote du titre : un choix ajoute
               * toutes les lignes du protocole, produits et quantites tels que
               * le Catalogue les a regles ; elles restent modifiables ensuite.
               */
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={chargement || protocoles.length === 0}
                  title={!chargement && protocoles.length === 0 ? 'Aucun protocole dans le Catalogue' : undefined}
                  aria-haspopup="listbox"
                  aria-expanded={protocolesOuverts}
                  onClick={() => setProtocolesOuverts((actuel) => !actuel)}
                >
                  Protocoles
                </Button>
                {protocolesOuverts ? (
                  /* Un ancre de la largeur de la liste, cale a droite : la liste du socle se pose dessous, sans deborder de l'ecran. */
                  <div className="pointer-events-none absolute right-0 top-0 h-full w-72 [&>*]:pointer-events-auto">
                    <OptionList
                      recherche
                      options={entreesDuChoix(
                        [],
                        protocoles.map((p) => ({
                          id: p.protocole.id,
                          name: p.protocole.name,
                          lignes: p.lignes.length,
                          prix: totalDesLignes(p.lignes.map((l) => ({ quantite: l.quantity, prix: prixDe(l.productId) }))),
                        })),
                        enEuros,
                      )}
                      value={null}
                      vide="Aucun protocole"
                      onPick={(valeur) => {
                        const choix = lireChoix(valeur);
                        setProtocolesOuverts(false);
                        if (choix?.sorte === 'protocole') void ajouterProtocole(choix.id);
                      }}
                      onClose={() => setProtocolesOuverts(false)}
                    />
                  </div>
                ) : null}
              </>
            )
          }
        >
          {chargement ? (
            <SkeletonText lines={5} />
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-soft text-left text-xs text-ink-500">
                  <th className="py-2 font-semibold">Produit</th>
                  <th className="w-36 py-2 font-semibold">Lot</th>
                  <th className="py-2 text-right font-semibold">Quantité</th>
                  <th className="w-28 py-2 pl-2 font-semibold">Unité</th>
                  <th className="py-2 text-right font-semibold">Prix TTC</th>
                  <th className="py-2 text-right font-semibold">Total</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {groupesDeLignes(lignes, rangDansLeProtocole).map((groupe) => (
                  <Fragment key={groupe.protocolId ?? 'seules'}>
                    {groupe.protocolId === null ? null : (
                      <tr>
                        <td colSpan={7} className="pb-1 pt-4 text-xs font-semibold text-ink-500">
                          {nomDuProtocole(groupe.protocolId)}
                        </td>
                      </tr>
                    )}
                    {groupe.lignes.map((ligne) => {
                      const s = sac(ligne);
                      const qte = Number(s['quantity'] ?? 1);
                      const prix = prixDe(s['productId']);
                      return (
                        <tr key={ligne.id} className="border-b border-border-soft">
                          <td className="py-2 text-ink-900">{produit(s['productId'])?.name ?? 'Produit'}</td>
                          <td className="py-2 pr-3">
                            {produitsDuCarnet.has(String(s['productId'] ?? '')) ? (
                              <ChampDuLot key={`${ligne.id}-lot`} valeur={valeurEnTexte(s['lotNumber'])} onEcrire={(lot) => void ecrireLot(ligne.id, lot)} />
                            ) : null}
                          </td>
                          <td className="py-2 text-right tabular-nums text-ink-700">{nombreEnTexte(qte)}</td>
                          <td className="py-2 pl-2 text-ink-700">{produit(s['productId'])?.unit ?? ''}</td>
                          <td className="py-2 text-right tabular-nums text-ink-700">{prix === null ? '' : enEuros(prix)}</td>
                          <td className="py-2 text-right tabular-nums text-ink-900">{prix === null ? '' : enEuros(prix * qte)}</td>
                          <td className="py-2 text-right">
                            {cloturee ? null : (
                              <Button variant="ghost" size="sm" aria-label="Retirer la ligne" onClick={() => void retirerLigne(ligne.id)}>
                                ×
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </Fragment>
                ))}
                <tr>
                  <td className="py-2 font-semibold text-ink-900" colSpan={5}>
                    Total indicatif
                  </td>
                  <td className="py-2 text-right font-semibold tabular-nums text-ink-900">{enEuros(totalDuJour)}</td>
                  <td />
                </tr>
              </tbody>
            </table>
          )}
          {cloturee ? (
            <p className="text-sm text-ink-500">Consultation clôturée : soins et produits figés.</p>
          ) : (
            <div className="flex items-center gap-2">
              <div className="relative min-w-0 flex-1">
                <Button
                  variant="secondary"
                  className="h-9 w-full justify-start gap-2 px-2.5 text-sm font-normal"
                  aria-haspopup="listbox"
                  aria-expanded={choixOuvert}
                  aria-label="Ajouter un produit"
                  onClick={() => setChoixOuvert((actuel) => !actuel)}
                  onKeyDown={(event) => {
                    if (event.key === 'ArrowDown' || (event.key === 'Enter' && produitChoisi === '')) {
                      event.preventDefault();
                      setChoixOuvert(true);
                    }
                  }}
                >
                  <IconSearch size={14} className="shrink-0 text-ink-400" />
                  <span className={cn('min-w-0 flex-1 truncate text-left', produitChoisi === '' ? 'text-ink-400' : 'text-ink-900')}>
                    {produitChoisi === '' ? 'Ajouter un produit…' : (produit(produitChoisi)?.name ?? 'Produit')}
                  </span>
                </Button>
                {choixOuvert ? (
                  <OptionList
                    recherche
                    options={entreesDuChoix(catalogue, [], enEuros)}
                    value={produitChoisi === '' ? null : `produit:${produitChoisi}`}
                    vide="Aucun produit"
                    onPick={(valeur) => {
                      const choix = lireChoix(valeur);
                      setChoixOuvert(false);
                      if (choix?.sorte === 'produit') setProduitChoisi(choix.id);
                    }}
                    onClose={() => setChoixOuvert(false)}
                  />
                ) : null}
              </div>
              <input
                className="h-9 w-16 rounded-sm border border-border bg-surface-1 px-2 text-right text-sm tabular-nums text-ink-900 focus-visible:outline-none focus-visible:border-accent-border"
                value={quantite}
                onChange={(e) => setQuantite(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void ajouterLigne();
                }}
                inputMode="decimal"
                aria-label="Quantité"
              />
              {/* L'UNITE DU CATALOGUE (ml, dose, seringue, flacon) : la quantite se lit dans cette unite. */}
              <span className={cn('w-24 shrink-0 truncate text-sm', produitChoisi !== '' && (produit(produitChoisi)?.unit ?? '') !== '' ? 'text-ink-700' : 'text-ink-400')}>
                {produitChoisi === '' ? 'unité' : produit(produitChoisi)?.unit || 'sans unité'}
              </span>
              <Button size="sm" disabled={produitChoisi === ''} onClick={() => void ajouterLigne()}>
                Ajouter
              </Button>
            </div>
          )}
        </Section>
      ) : null}

      {onglet === 'historique' && chargement ? (
        <Section titre="Historique">
          <SkeletonText lines={5} />
        </Section>
      ) : null}
      {onglet === 'historique' && !chargement ? (
        <OngletHistorique sdk={sdk} consultations={historique} vide="Première consultation de cet animal." onOuvrirConsultation={props.onOuvrirConsultation} />
      ) : null}

      {onglet === 'rappels' && chargement ? (
        <Section titre="Vaccins et rappels">
          <SkeletonText lines={5} />
        </Section>
      ) : null}
      {onglet === 'rappels' && !chargement ? (
        <Carnet
          espece={valeurEnTexte(fiche['species'])}
          lignes={carnet}
          produitsPour={(protocole) => produitsPour(protocole, fiche['species'], produitsDuCarnet)}
          peutAjouter={!cloturee}
          onAjouter={ajouterDepuisLeCarnet}
        />
      ) : null}

      {onglet === 'taches' && chargement ? (
        <Section titre="Tâches du dossier">
          <SkeletonText lines={4} />
        </Section>
      ) : null}
      {onglet === 'taches' && !chargement ? (
        <OngletTaches
          taches={taches}
          animal={animal?.name ?? ''}
          personnes={personnes}
          assigneParDefaut={assigneParDefaut}
          nomDePersonne={nomDePersonne}
          onBasculer={(t) => void dossier.basculerTache(t)}
          onOuvrir={ouvrirTache}
          onCreer={creerSuite}
        />
      ) : null}
      {onglet === 'doses' && chargement ? (
        <Section titre="Calculateur de dose">
          <SkeletonText lines={4} />
        </Section>
      ) : null}
      {onglet === 'doses' && !chargement ? (
        <CalculateurDeDose
          sdk={sdk}
          espece={valeurEnTexte(fiche['species'])}
          poidsDeLaFiche={dossier.poids[dossier.poids.length - 1]?.kg ?? (typeof fiche['weightKg'] === 'number' ? fiche['weightKg'] : null)}
          ageDeLaFiche={ageEnAnnees(fiche['birthDate'], quand)}
          catalogue={catalogue}
        />
      ) : null}
    </div>
  );
}

/**
 * LA VUE : le SDK et l'org viennent du runtime, l'annuaire et la navigation du
 * shell (ports de `@taki/ui`). Sans fiche ouverte, rien ne se dessine ; une
 * autre fiche remonte le dossier (`key`), donc ses onglets et ses brouillons.
 */
export function DossierDeConsultation(props: ProprietesDeVueCanvas): JSX.Element | null {
  const { fiche, lecture, onPatch, onOpenRecord } = props;
  const { sdk, orgId = '' } = useAppRuntime();
  const navigation = useNavigationPort();
  const annuaire = usePersonnesPort();
  const [personnes, setPersonnes] = useState<readonly PersonneVue[]>([]);
  useEffect(() => {
    if (annuaire === null) return;
    let vivant = true;
    void annuaire.lister().then(
      (liste) => vivant && setPersonnes(liste),
      () => undefined,
    );
    return () => {
      vivant = false;
    };
  }, [annuaire]);
  if (fiche === null) return null;
  const consultation = consultationLue(fiche, lecture);

  /* UNE ECRITURE DE LA CONSULTATION OUVERTE : le chemin de l'edition en place. Un choix multiple part en une cle par ligne. */
  const ecrireChamp = (cle: string, valeur: string | string[] | Date | null): void => {
    void onPatch(cle, Array.isArray(valeur) ? valeur.join('\n') : valeur);
  };
  /* OUVRIR UNE FICHE LIEE : l'app qui montre cette famille, trouvee par le shell. */
  const onOuvrirFiche = (family: string, type: string, id: string, depuis: string | null): void => {
    const destination = navigation?.destination(family, type) ?? null;
    if (navigation !== null && destination !== null) navigation.ouvrir(destination, id, depuis);
  };
  const destinationDeLAnimal = navigation?.destination('assets', 'animal') ?? null;
  return (
    <Dossier
      key={String(fiche['id'])}
      sdk={sdk}
      orgId={orgId}
      consultation={consultation}
      lecture={lecture}
      ecrireChamp={ecrireChamp}
      personnes={personnes}
      moi={annuaire?.moi?.personId ?? ''}
      onOuvrirConsultation={(id) => {
        const destination = navigation?.destination('careRecords', 'consultation') ?? null;
        if (navigation !== null && destination !== null) navigation.ouvrir(destination, id, lecture?.titre ?? null);
        else onOpenRecord(id);
      }}
      onOuvrirFiche={onOuvrirFiche}
      poserActions={props.poserActions}
      onOuvrirDossierDeLAnimal={
        navigation === null || destinationDeLAnimal === null ? null : (animalId, nom) => navigation.ouvrir(destinationDeLAnimal, animalId, nom)
      }
    />
  );
}

export default DossierDeConsultation;
